import { useEffect, useRef, useState } from "react";
import {
  EMBED_DEFAULTS,
  createEmbed,
  listEmbeds,
  updateEmbed,
  type EmbedOptions,
  type VaultEmbed,
} from "../vault/vaultApi";
import { readImageAsDataUrl } from "../shared/imageUpload";
import { useToast } from "../../shared/ui";

/**
 * The embed's option bag, its persistence, and the `<iframe>` snippet.
 *
 * Shared by the Vault's Embed modal and the studio's Embed / Branding panels,
 * because two surfaces editing the same stored object from two copies of this
 * logic is exactly the drift `rails/railConfig.ts` already documents for the
 * Poses and Advanced panels: each seeds its own local state once, so editing
 * one leaves the others stale and the user cannot tell which is real.
 *
 * Autosave is opt-in. The modal has an explicit Save button and a close to
 * chain off it; a docked panel has neither, so it debounces instead.
 */
/**
 * The model's shared-link embed: the first one it has, or a new one. The one
 * rule every surface uses, so the studio's "Open customer link" opens the same
 * link the Embed panel shows.
 */
export async function resolveEmbed(modelId: string): Promise<VaultEmbed> {
  const existing = await listEmbeds(modelId);
  return existing[0] ?? (await createEmbed(modelId, [])).embed;
}

/** The public URL a shopper opens for an embed. */
export const embedUrlOf = (embed: Pick<VaultEmbed, "token">) => `${window.location.origin}/embed/${embed.token}`;

export function useEmbedOptions(modelId: string | undefined, { autosave = false } = {}) {
  const { toast } = useToast();
  const [embed, setEmbed] = useState<VaultEmbed | null>(null);
  const [opts, setOpts] = useState<EmbedOptions>({ ...EMBED_DEFAULTS });
  const [loading, setLoading] = useState(!!modelId);
  const [saving, setSaving] = useState(false);
  /** Suppresses the autosave that would otherwise fire on the initial load. */
  const hydrated = useRef(false);

  useEffect(() => {
    if (!modelId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    hydrated.current = false;
    void (async () => {
      try {
        const e = await resolveEmbed(modelId);
        if (!alive) return;
        setEmbed(e);
        setOpts({ ...EMBED_DEFAULTS, ...(e.options ?? {}) });
      } catch {
        /* leave defaults — an unauthenticated route is a normal outcome here */
      } finally {
        if (alive) { setLoading(false); hydrated.current = true; }
      }
    })();
    return () => { alive = false; };
  }, [modelId]);

  const set = (patch: Partial<EmbedOptions>) => setOpts((o) => ({ ...o, ...patch }));

  const save = async (): Promise<boolean> => {
    if (!embed) return false;
    setSaving(true);
    try {
      const r = await updateEmbed(embed._id, embed.allowedDomains, opts);
      setEmbed(r.embed);
      return true;
    } catch (e) {
      toast({ tone: "error", title: "Save failed", description: e instanceof Error ? e.message : undefined });
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!autosave || !embed || !hydrated.current) return;
    const t = setTimeout(() => { void save(); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosave, embed?._id, opts]);

  const uploadLogo = async (file?: File) => {
    if (!file) return;
    try {
      const dataUrl = await readImageAsDataUrl(file, 256);
      set({ logo: { ...(opts.logo ?? {}), dataUrl, show: true } });
    } catch (e) {
      toast({ tone: "error", title: "Couldn't read logo", description: e instanceof Error ? e.message : undefined });
    }
  };

  const embedUrl = embed ? embedUrlOf(embed) : "";
  const snippet = embed
    ? `<iframe src="${embedUrl}" width="${opts.responsive ? "100%" : opts.width}" height="${opts.responsive ? 600 : opts.height}" frameborder="0" allow="xr-spatial-tracking; fullscreen" allowfullscreen></iframe>`
    : "";

  return { embed, opts, set, save, saving, loading, uploadLogo, snippet, embedUrl };
}
